/*
 * Shrine on Horizon — core storefront script.
 * Clean rewrite of the components Shrine shipped in main.js / secondary.js:
 * pub/sub, focus helpers, cart (page + drawer + upsells), product form,
 * predictive search, header drawer & menus, modals and small UI widgets.
 * Loaded once from layout/theme.liquid (classic deferred script).
 */

/**
 * Modals, drawers and zoom panes move to the end of <body>. Shrine styles only apply
 * inside a .shrine wrapper, so they move into this shared wrapper instead.
 */
window.shPortal = window.shPortal || function () {
  let portal = document.getElementById('sh-portal');
  if (!portal) {
    portal = document.createElement('div');
    portal.id = 'sh-portal';
    portal.className = 'shrine';
    document.body.appendChild(portal);
  }
  return portal;
};


/* ---------- Environment ---------- */

const defaultDirection = document.documentElement.dir === 'rtl' || document.body?.dataset.rtl === 'true' ? 'rtl' : 'ltr';
const isRtl = defaultDirection === 'rtl';
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) subscribers[eventName] = [];
  subscribers[eventName] = [...subscribers[eventName], callback];
  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) subscribers[eventName].forEach((callback) => callback(data));
}

/* ---------- Utilities ---------- */

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  if (!container) return;
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (!elementToFocus) return;
  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('sh-media-gallery template video').forEach((video) => video.pause());
  document.querySelectorAll('sh-product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}
window.pauseAllMedia = pauseAllMedia;

function formatMoney(cents, format, hideZeroDecimals = false) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.moneyFormat || '${{amount}}';

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number == null) return 0;
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    let centsPart = parts[1] ? decimal + parts[1] : '';
    if (hideZeroDecimals && centsPart === decimal + '00') centsPart = '';
    return dollars + centsPart;
  }

  const match = formatString.match(placeholderRegex);
  if (!match) return formatString;
  let value = '';
  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }
  return formatString.replace(placeholderRegex, value);
}

function displayPrices(price, comparePrice, priceSpan, comparePriceSpan, moneyFormat) {
  if (!moneyFormat) return;
  if (price && priceSpan) priceSpan.innerHTML = formatMoney(price, moneyFormat);
  if (comparePrice && comparePriceSpan) {
    comparePriceSpan.innerHTML = formatMoney(comparePrice, moneyFormat);
    comparePriceSpan.classList.toggle('hidden', !(comparePrice > price));
  }
}

/** Marks secondary dropdown options as unavailable / non-existent for the selected first option. */
function updateVariantStatuses(variants, selects) {
  selects.forEach((select, index) => {
    if (index === 0) return;
    const options = [...select.querySelectorAll('option')];
    const previousValue = selects[index - 1].querySelector(':checked').value;
    const available = variants
      .filter((v) => v.available && v[`option${index}`] === previousValue)
      .map((v) => v[`option${index + 1}`]);
    const existing = variants.filter((v) => v[`option${index}`] === previousValue).map((v) => v[`option${index + 1}`]);

    options.forEach((option) => {
      option.classList.remove('unavailable', 'non-existent');
      const value = option.getAttribute('value');
      if (existing.includes(value)) {
        if (available.includes(value)) {
          option.innerText = value;
        } else {
          option.innerText = window.variantStrings.unavailable_with_option.replace('[value]', value);
          option.classList.add('unavailable');
        }
      } else {
        option.innerText = window.variantStrings.unavailable_with_option.replace('[value]', value);
        option.classList.add('non-existent');
      }
    });
  });
}

/** Moves the selection to the closest valid option value. Returns true when a change was dispatched. */
function findAvailableVariant(container, options, isMainPicker = true, skipNonExistent = false, skipUnavailable = false) {
  for (let i = options.length - 1; i >= 0; i--) {
    const wrapper = isMainPicker
      ? container.querySelector(`.product-form__input:nth-child(${i + 1})`)
      : container.querySelectorAll('.select')[i];
    if (!wrapper) continue;
    const type = isMainPicker ? wrapper.querySelector('.product-form__input__type').dataset.type : 'dropdown';
    const inputs = type === 'dropdown' ? wrapper.querySelectorAll('option') : wrapper.querySelectorAll('input[type="radio"]');
    const isValid = (input) =>
      (!skipNonExistent || !input.classList.contains('non-existent')) &&
      (!skipUnavailable || !input.classList.contains('unavailable'));

    let index = Array.from(inputs).findIndex((input) => input.value === options[i]);
    let found = false;
    while (index < inputs.length - 1) {
      index++;
      if (isValid(inputs[index])) {
        options[i] = inputs[index].value;
        found = true;
        break;
      }
    }
    if (!found) {
      index = Array.from(inputs).findIndex((input) => input.value === options[i]);
      while (index > 0) {
        index--;
        if (isValid(inputs[index])) {
          options[i] = inputs[index].value;
          found = true;
          break;
        }
      }
    }
    if (found) {
      if (type === 'dropdown') {
        wrapper.querySelector('.select__select').value = options[i];
      } else {
        wrapper.querySelector(`input[type="radio"][value="${CSS.escape(options[i])}"]`).checked = true;
      }
      container.dispatchEvent(new Event('change'));
      return true;
    }
  }
  return false;
}

/* ---------- Shopify helpers (address forms) ---------- */

if (typeof window.Shopify === 'undefined') window.Shopify = {};

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    const option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener(eventName, callback, false);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  const method = options.method || 'post';
  const params = options.parameters || {};
  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);
  for (const key in params) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (countryDomId, provinceDomId, options) {
  this.countryEl = document.getElementById(countryDomId);
  this.provinceEl = document.getElementById(provinceDomId);
  this.provinceContainer = document.getElementById(options.hideElement || provinceDomId);
  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    const value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },
  initProvince: function () {
    const value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) Shopify.setSelectorByValue(this.provinceEl, value);
  },
  countryHandler: function () {
    const opt = this.countryEl.options[this.countryEl.selectedIndex];
    const provinces = JSON.parse(opt.getAttribute('data-provinces'));
    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (let i = 0; i < provinces.length; i++) {
        const option = document.createElement('option');
        option.value = provinces[i][0];
        option.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(option);
      }
      this.provinceContainer.style.display = '';
    }
  },
  clearOptions: function (selector) {
    while (selector.firstChild) selector.removeChild(selector.firstChild);
  },
  setOptions: function (selector, values) {
    for (let i = 0; i < values.length; i++) {
      const option = document.createElement('option');
      option.value = values[i];
      option.innerHTML = values[i];
      selector.appendChild(option);
    }
  },
};

/* ---------- Accessibility for <details> ---------- */

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));
  if (summary.nextElementSibling?.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }
  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });
  if (summary.closest('sh-header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

/* ---------- Quantity input ---------- */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.quantityGifts = document.getElementById(`quantity-gifts-${this.dataset.section}`);
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    if (event.currentTarget.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const minus = this.querySelector(".quantity__button[name='minus']");
      minus?.classList.toggle('disabled', value <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const plus = this.querySelector(".quantity__button[name='plus']");
      plus?.classList.toggle('disabled', value >= parseInt(this.input.max));
    }
    if (this.quantityGifts?.unlockGifts) this.quantityGifts.unlockGifts(value);
  }
}
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

/* ---------- Cart ---------- */

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (this.clearCart) {
        cartItems.clearCart();
      } else {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}
if (!customElements.get('cart-remove-button')) customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    this.secondCartItems = document.querySelector('cart-drawer-items');
    const debouncedOnChange = debounce((event) => this.onChange(event), ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    if (!event.target.dataset.index) return;
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    const mainCartItems = document.getElementById('main-cart-items');
    if (!mainCartItems) return;
    fetch(`${routes.cart_url}?section_id=${mainCartItems.dataset.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => console.error(e));
  }

  updateCart() {
    const body = JSON.stringify({
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => {
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        this.renderSections(parsedState);
      })
      .catch((e) => console.error(e));
  }

  getSectionsToRender() {
    const sections = [
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
    ];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartItems) {
      sections.unshift({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
    }
    if (mainCartFooter) {
      sections.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
    }
    return sections;
  }

  renderSections(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const host = document.getElementById(section.id);
      if (!host || !parsedState.sections?.[section.section]) return;
      const target = host.querySelector(section.selector) || host;
      target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    });
  }

  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body });
      const parsedState = JSON.parse(await response.text());
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      const items = document.querySelectorAll('.cart-item');

      if (parsedState.errors) {
        if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, parsedState.errors);
        return;
      }

      if (!parsedState.sections) {
        const sectionsResponse = await fetch(
          `${routes.cart_url}?sections=${this.getSectionsToRender()
            .map((section) => section.section)
            .join(',')}`
        );
        parsedState.sections = await sectionsResponse.json();
      }

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartDrawer = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      if (cartDrawer) cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);

      this.renderSections(parsedState);

      const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
      let message = '';
      if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
        message =
          typeof updatedValue === 'undefined'
            ? window.cartStrings.error
            : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
      }
      this.updateLiveRegions(line, message);

      const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        if (cartDrawer) {
          trapFocus(cartDrawer, lineItem.querySelector(`[name="${name}"]`));
        } else {
          lineItem.querySelector(`[name="${name}"]`).focus();
        }
      } else if (parsedState.item_count === 0 && cartDrawer) {
        trapFocus(cartDrawer.querySelector('.drawer__inner-empty'), cartDrawer.querySelector('a'));
      } else if (document.querySelector('.cart-item') && cartDrawer) {
        trapFocus(cartDrawer, document.querySelector('.cart-item__name'));
      }

      if (cartDrawer) {
        cartDrawer.checkForClear();
        cartDrawer.querySelector('countdown-timer')?.playTimer();
        cartDrawer.syncGifts();
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
    } catch (error) {
      console.error(error);
      this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
    } finally {
      this.disableLoading(line);
      if (this.secondCartItems && this.secondCartItems !== this && this.secondCartItems.updateCart) {
        this.secondCartItems.updateCart();
      }
      document.dispatchEvent(new CustomEvent('cartQuantityUpdated', { detail: { handle: 'handle' } }));
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    const errorText = lineItemError?.querySelector('.cart-item__error-text');
    if (errorText) errorText.innerHTML = message;

    this.lineItemStatusElement?.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
  }

  getSectionInnerHTML(html, selector) {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');
    const overlays = [
      ...this.querySelectorAll(`#CartItem-${line} .loading-overlay`),
      ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`),
    ];
    overlays.forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');
    const overlays = [
      ...this.querySelectorAll(`#CartItem-${line} .loading-overlay`),
      ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`),
    ];
    overlays.forEach((overlay) => overlay.classList.add('hidden'));
  }

  clearCart() {
    const body = JSON.stringify({
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    fetch(`${routes.cart_clear_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.add('is-empty');
        const cartDrawer = document.querySelector('cart-drawer');
        document.getElementById('main-cart-footer')?.classList.add('is-empty');
        cartDrawer?.classList.add('is-empty');
        this.renderSections(parsedState);
        if (cartDrawer) trapFocus(cartDrawer.querySelector('.drawer__inner-empty'), cartDrawer.querySelector('a'));
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      });
  }
}
if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

if (!customElements.get('sh-cart-note')) {
  customElements.define(
    'sh-cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

/** Copies client-side state (selected upsells, chosen variants, running timer) into freshly fetched drawer HTML. */
function preserveDrawerState(currentRoot, newRoot) {
  if (!newRoot) return;
  const newTimer = newRoot.querySelector('.cart-timer');
  const oldTimer = currentRoot.querySelector('.cart-timer');
  if (newTimer && oldTimer) newTimer.innerHTML = oldTimer.innerHTML;

  const selector = 'cart-drawer-upsell[data-toggle="true"], cart-drawer-gift';
  const currentUpsells = currentRoot.querySelectorAll(selector);
  const newUpsells = newRoot.querySelectorAll(selector);

  currentUpsells.forEach((current, index) => {
    const next = newUpsells[index];
    if (!next) return;
    if (current.nodeName.toLowerCase() === 'cart-drawer-upsell') next.dataset.selected = current.dataset.selected;
    next.dataset.id = current.dataset.id;
    const nextId = next.querySelector('[name="id"]');
    const currentId = current.querySelector('[name="id"]');
    if (nextId && currentId) nextId.value = currentId.value;

    const nextImg = next.querySelector('.upsell__image__img');
    const currentImg = current.querySelector('.upsell__image__img');
    if (nextImg && currentImg) nextImg.src = currentImg.src;

    if (next.querySelector('.upsell__variant-picker')) {
      const currentSelects = current.querySelectorAll('.select__select');
      next.querySelectorAll('.select__select').forEach((select, i) => {
        if (!currentSelects[i]) return;
        select.value = currentSelects[i].value;
        select.querySelectorAll('option').forEach((option) => {
          option.removeAttribute('selected');
          if (option.value === currentSelects[i].value.trim()) option.setAttribute('selected', '');
        });
      });
    }

    if (current.dataset.updatePrices === 'true') {
      const nextPrice = next.querySelector('.upsell__price');
      const currentPrice = current.querySelector('.upsell__price');
      if (nextPrice && currentPrice) nextPrice.innerHTML = currentPrice.innerHTML;
    }
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.upsellHandles = this.getUpsellHandles();
    this.checkForClear();
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => this.classList.add('animate', 'active'));

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('countdown-timer')?.playTimer();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  getUpsellHandles() {
    const handles = [];
    this.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach((upsell) => {
      if (upsell.dataset.handle) handles.push(upsell.dataset.handle);
    });
    return handles;
  }

  oneNonUpsellRemaining() {
    const items = this.querySelectorAll('.cart-item');
    let upsellCount = 0;
    items.forEach((item) => {
      this.upsellHandles.forEach((handle) => {
        if (item.classList.contains(`cart-item--product-${handle}`)) upsellCount++;
      });
    });
    return items.length - upsellCount <= 1;
  }

  checkForClear() {
    const clear = this.oneNonUpsellRemaining();
    this.querySelectorAll('cart-remove-button').forEach((button) => {
      button.clearCart = clear;
    });
  }

  /** Adds or removes auto-gifts so they match the current cart. */
  syncGifts() {
    const toAdd = [];
    const toRemove = [];
    this.querySelectorAll('cart-drawer-gift').forEach((gift) => {
      if (!gift.getUpdateRequired()) return;
      if (this.querySelector(`.cart-item--product-${gift.dataset.handle}`)) {
        if (gift.dataset.selected === 'false') toRemove.push(gift);
      } else if (gift.dataset.selected === 'true') {
        toAdd.push(gift);
      }
    });
    if (toRemove.length > 0) {
      toRemove[0].removeFromCart();
    } else if (toAdd.length > 0) {
      toAdd[0].addToCart();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, silent = false) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.checkForClear();
    this.querySelector('countdown-timer')?.playTimer();
    this.syncGifts();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (silent) return;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!element) return '';
    if (selector === '#CartDrawer') preserveDrawerState(this, element);
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
    this.secondCartItems = document.querySelector('cart-items');
  }

  updateCart() {
    fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ updates: {}, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
    })
      .then((response) => response.json())
      .then((parsedState) => document.querySelector('cart-drawer').renderContents(parsedState, true))
      .catch((e) => console.error(e));
  }

  getSectionInnerHTML(html, selector) {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!element) return '';
    if (selector === '.drawer__inner') preserveDrawerState(this.cartDrawer, element);
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}
if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

/** Bridges Horizon-native add-to-cart events (product cards, quick add) into the Shrine cart drawer. */
document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.source === 'shrine') return;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  fetch(`${routes.cart_update_url}`, {
    ...fetchConfig(),
    body: JSON.stringify({ updates: {}, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
  })
    .then((response) => response.json())
    .then((parsedState) => {
      drawer.classList.toggle('is-empty', parsedState.item_count === 0);
      drawer.renderContents(parsedState, event.detail?.data?.didError === true);
    })
    .catch((e) => console.error(e));
});

class CartDiscountBlock extends HTMLElement {
  static pendingSuccess = false;

  constructor() {
    super();
    this.form = this.querySelector('.cart-discount-form');
    this.input = this.querySelector('input[name="cart-discount-field"]');
    this.submitBtn = this.querySelector('.cart-discount-form__submit');
    this.spinner = this.querySelector('.cart-discount-form__submit .loading-overlay__spinner');
    this.submitLabel = this.querySelector('.cart-discount-form__submit-label');
    this.errorEmpty = this.querySelector('.cart-discount-form__error--empty');
    this.errorInvalid = this.querySelector('.cart-discount-form__error--invalid');
    this.errorDuplicate = this.querySelector('.cart-discount-form__error--duplicate');
    this.successMsg = this.querySelector('.cart-discount-form__success');
    this.submitting = false;

    this.hideErrors();
    this.form?.addEventListener('submit', this.handleSubmit.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));
    this.input?.addEventListener('input', this.hideErrors.bind(this));

    if (CartDiscountBlock.pendingSuccess) {
      CartDiscountBlock.pendingSuccess = false;
      this.showSuccess();
    }
  }

  hideErrors() {
    [this.errorEmpty, this.errorInvalid, this.errorDuplicate].forEach((el) => {
      if (el) el.style.display = 'none';
    });
  }

  showError(which) {
    this.hideErrors();
    const target = { empty: this.errorEmpty, invalid: this.errorInvalid, duplicate: this.errorDuplicate }[which];
    if (target) target.style.display = 'block';
  }

  showSuccess() {
    if (!this.successMsg || this.dataset.successEnabled !== 'true') return;
    this.successMsg.style.display = 'block';
    const duration = parseInt(this.dataset.successDuration, 10) || 2000;
    clearTimeout(this._successTimer);
    this._successTimer = setTimeout(() => {
      if (this.successMsg) this.successMsg.style.display = 'none';
    }, duration);
  }

  setLoading(loading) {
    this.submitting = loading;
    if (this.submitBtn) {
      this.submitBtn.disabled = loading;
      this.submitBtn.classList.toggle('loading', loading);
    }
    this.spinner?.classList.toggle('hidden', !loading);
    if (this.submitLabel) this.submitLabel.style.visibility = loading ? 'hidden' : '';
  }

  existingCodes() {
    const fromAttr = (this.dataset.appliedCodes || '').trim();
    if (fromAttr) return fromAttr.split(',').map((c) => c.trim()).filter(Boolean);
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .map((pill) => pill.dataset.discountCode)
      .filter(Boolean);
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;
    this.hideErrors();
    const code = (this.input.value || '').trim();
    if (!code) {
      this.showError('empty');
      return;
    }
    const existing = this.existingCodes();
    if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
      this.showError('duplicate');
      return;
    }
    this.setLoading(true);
    const data = await this.postDiscount([...existing, code].join(','));
    if (!data) {
      this.setLoading(false);
      return;
    }
    const submitted = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
    const applicable = submitted && submitted.applicable !== false;
    const applicableCodes = (data.discount_codes || []).filter((d) => d.applicable);
    if (!applicable || applicableCodes.length <= existing.length) {
      this.showError('invalid');
      this.input.value = '';
      await this.postDiscount(existing.join(','));
      this.setLoading(false);
      return;
    }
    this.input.value = '';
    CartDiscountBlock.pendingSuccess = true;
    this.refreshCart(data);
  }

  handleClick(event) {
    const removeBtn = event.target.closest('.cart-discount__pill-remove');
    if (!removeBtn) return;
    event.preventDefault();
    if (this.submitting) return;
    const code = removeBtn.closest('.cart-discount__pill')?.dataset.discountCode;
    if (code) this.removeDiscount(code);
  }

  async removeDiscount(code) {
    this.setLoading(true);
    const remaining = this.existingCodes().filter((c) => c !== code);
    const data = await this.postDiscount(remaining.join(','));
    if (data) {
      this.refreshCart(data);
    } else {
      this.setLoading(false);
    }
  }

  async postDiscount(discount) {
    try {
      const response = await fetch(`${routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount, sections: ['cart-drawer', 'cart-icon-bubble'] }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  refreshCart(cartData) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(cartData);
      drawer.classList.remove('is-empty');
    } else {
      window.location.reload();
    }
  }
}
if (!customElements.get('cart-discount-block')) customElements.define('cart-discount-block', CartDiscountBlock);

/** Legacy discount form (checkout redirect) used by the classic cart footer. */
function handleDiscountForm(event) {
  event.preventDefault();
  const input = event.target.querySelector('[name=cart-discount-field]');
  const error = event.target.querySelector('.cart-discount-form__error');
  const code = input.value;
  if (code === undefined || code.length === 0) {
    error.style.display = 'block';
    return;
  }
  error.style.display = 'none';
  window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
}

function handleDiscountFormChange() {
  document.querySelectorAll('.cart-discount-form__error').forEach((error) => {
    error.style.display = 'none';
  });
}

/* ---------- Upsells & gifts (product page + cart drawer) ---------- */

class ProductInfoUpsell extends HTMLElement {
  constructor() {
    super();
    this.image = this.querySelector('.upsell__image__img');
    this.toggleBtn = this.querySelector('.upsell-toggle-btn');
    this.variantSelects = this.querySelector('.upsell__variant-picker');
    this.variantSelectElements = this.querySelectorAll('.select__select');
    this.jsonData = this.querySelector('[type="application/json"]');
    this.productForm = this.querySelector('product-form');
    if (this.productForm) this.idInput = this.productForm.querySelector('[name="id"]');
    this.skipNonExistent = this.dataset.skipNonExistent === 'true';
    this.skipUnavailable = this.dataset.skipUnavailable === 'true';
    this.updatePrices = this.dataset.updatePrices === 'true';

    if (this.updatePrices) {
      this.price = parseInt(this.dataset.price);
      this.comparePrice = parseInt(this.dataset.comparePrice);
      this.priceSpan = this.querySelector('.upsell__price .regular-price');
      this.comparePriceSpan = this.querySelector('.upsell__price .compare-price');
      this.percentageLeft = parseFloat(this.dataset.percentageLeft);
      this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
      this.moneyFormat = this.dataset.moneyFormat;
      this.isMainOfferItem = this.dataset.mainOfferItem === 'true';
      if (this.isMainOfferItem) this.mainOfferContainer = document.querySelector(`#MainBundleOffer-${this.dataset.section}`);
    }

    this.toggleBtn?.addEventListener('click', this.handleToggle.bind(this));
    this.variantSelects?.addEventListener('change', this.handleSelectChange.bind(this));
  }

  handleToggle(event) {
    const tag = event.target.nodeName.toLowerCase();
    if (tag === 'select' || tag === 'option') return;
    this.dataset.selected = this.dataset.selected === 'true' ? 'false' : 'true';
  }

  handleSelectChange(event) {
    const picker = event.currentTarget;
    const options = Array.from(picker.querySelectorAll('select'), (select) => select.value);
    const variant = this.getVariantData().find(
      (v) => !v.options.map((value, index) => options[index] === value).includes(false)
    );
    const sameFirstOption = this.getVariantData().filter((v) => picker.querySelector(':checked').value === v.option1);
    updateVariantStatuses(sameFirstOption, [...picker.querySelectorAll('select')]);

    if (!variant) {
      if (this.skipNonExistent) findAvailableVariant(picker, options, false, true, this.skipUnavailable);
      return;
    }
    if (this.skipUnavailable && !variant.available) {
      if (findAvailableVariant(picker, options, false, true, true)) return;
    }

    if (this.updatePrices) {
      this.price = variant.price * this.percentageLeft - this.fixedDiscount;
      this.comparePrice = variant.price;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) this.comparePrice = variant.compare_at_price;
      displayPrices(this.price, this.comparePrice, this.priceSpan, this.comparePriceSpan, this.moneyFormat);
    }
    if (this.image && variant.featured_image) this.image.src = variant.featured_image.src;
    this.updateId(variant.id);
    if (this.isMainOfferItem && this.mainOfferContainer?.updateTotalPrices) this.mainOfferContainer.updateTotalPrices();
  }

  updateId(id) {
    this.dataset.id = id;
    if (this.idInput) this.idInput.value = id;
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.jsonData.textContent);
    return this.variantData;
  }
}
if (!customElements.get('product-info-upsell')) customElements.define('product-info-upsell', ProductInfoUpsell);

class CartDrawerUpsell extends ProductInfoUpsell {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartItems = this.cartDrawer?.querySelector('cart-drawer-items');
    this.classicAddButton = this.querySelector('.upsell__add-btn');
  }

  handleToggle(event) {
    const tag = event.target.nodeName.toLowerCase();
    if (tag === 'select' || tag === 'option') return;
    if (this.dataset.selected === 'true') {
      this.dataset.selected = 'false';
      this.removeFromCart();
    } else {
      this.dataset.selected = 'true';
      this.addToCart();
    }
  }

  addRemoveFromCart() {
    if (this.dataset.selected === 'true' && !this.cartDrawer.classList.contains('is-empty')) {
      this.addToCart();
    } else {
      this.removeFromCart();
    }
  }

  inCart() {
    return this.cartDrawer?.querySelector(`.cart-item--product-${this.dataset.handle}`);
  }

  lockControls() {
    this.toggleBtn?.setAttribute('disabled', '');
    this.variantSelectElements.forEach((select) => select.setAttribute('disabled', ''));
  }

  addToCart() {
    if (this.inCart() || !this.productForm) return;
    this.lockControls();
    this.productForm.handleSubmit();
  }

  removeFromCart() {
    const item = this.inCart();
    if (!item || !this.cartItems) return;
    this.lockControls();
    this.cartItems.updateQuantity(item.dataset.index, 0);
  }

  updateId(id) {
    this.dataset.id = id;
    if (this.idInput) this.idInput.value = id;
    if (this.classicAddButton) return;
    if (this.dataset.selected !== 'true') return;
    this.removeFromCart();
    const onUpdated = (event) => {
      if (event.detail.handle !== 'handle') return;
      this.addToCart();
      document.removeEventListener('cartQuantityUpdated', onUpdated);
    };
    document.addEventListener('cartQuantityUpdated', onUpdated);
  }

  getUpdateRequired() {
    const inCart = this.inCart();
    return (inCart && this.dataset.selected === 'false') || (!inCart && this.dataset.selected === 'true');
  }
}
if (!customElements.get('cart-drawer-upsell')) customElements.define('cart-drawer-upsell', CartDrawerUpsell);

class CartDrawerGift extends CartDrawerUpsell {}
if (!customElements.get('cart-drawer-gift')) customElements.define('cart-drawer-gift', CartDrawerGift);

document.querySelector('cart-drawer')
  ?.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift')
  .forEach((upsell) => upsell.addRemoveFromCart?.());

/* ---------- Cart goal bars (multi-currency aware) ---------- */

function formatPresentmentMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(
      cents / 100
    );
  } catch (error) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function getPresentmentRate(shopCurrency) {
  const active = window.Shopify?.currency?.active;
  const rate = window.Shopify?.currency?.rate;
  if (!active || !rate || active === shopCurrency) return null;
  const parsed = parseFloat(rate);
  return parsed && parsed !== 1 ? { active, rate: parsed } : null;
}

class CartProgressBar extends HTMLElement {
  connectedCallback() {
    this.update();
  }

  update() {
    if (this.dataset.isQuantity === 'true') return;
    const rate = getPresentmentRate(this.dataset.shopCurrency);
    if (!rate) return;
    const goal = parseFloat(this.dataset.goalShopCents) * rate.rate;
    const total = parseFloat(this.dataset.cartTotalCents);
    const percentage = Math.min(100, (total / goal) * 100);
    const complete = this.dataset.subAutoComplete === 'true' || percentage >= 100;
    const bar = this.querySelector('.cart-progress__bar__progress');
    if (bar) bar.style.width = `${complete ? 100 : percentage}%`;
    const text = this.querySelector('.cart-progress__text');
    if (!text) return;
    if (complete) {
      text.innerHTML = this.dataset.successMessage || '';
    } else {
      const remaining = formatPresentmentMoney(Math.max(0, goal - total), rate.active);
      text.innerHTML = (this.dataset.progressMessage || '').replace('[amount]', `<span>${remaining}</span>`);
    }
  }
}
if (!customElements.get('cart-progress-bar')) customElements.define('cart-progress-bar', CartProgressBar);

class CartCheckpointsBar extends HTMLElement {
  connectedCallback() {
    this.update();
  }

  update() {
    if (this.dataset.isQuantity === 'true') return;
    const rate = getPresentmentRate(this.dataset.shopCurrency);
    if (!rate) return;
    let goals;
    try {
      goals = JSON.parse(this.dataset.goals || '[]');
    } catch (error) {
      return;
    }
    if (!goals.length) return;
    const total = parseFloat(this.dataset.cartTotalCents);
    const lastGoal = goals[goals.length - 1].cents * rate.rate;
    let next = null;
    for (const goal of goals) {
      const cents = goal.cents * rate.rate;
      if (cents > total) {
        next = { cents, text: goal.text };
        break;
      }
    }
    const bar = this.querySelector('.cart-progress__bar__progress');
    if (bar) bar.style.width = `${next ? Math.min(100, (total / lastGoal) * 100) : 100}%`;
    const text = this.querySelector('.cart-progress__text');
    if (!text) return;
    if (next) {
      const remaining = formatPresentmentMoney(Math.max(0, next.cents - total), rate.active);
      text.innerHTML = (this.dataset.progressMessage || '')
        .replace('[amount]', `<span>${remaining}</span>`)
        .replace('[next_goal]', next.text || '');
    } else {
      text.innerHTML = this.dataset.successMessage || '';
    }
  }
}
if (!customElements.get('cart-checkpoints-bar')) customElements.define('cart-checkpoints-bar', CartCheckpointsBar);

class TncCheckbox extends HTMLElement {
  constructor() {
    super();
    this.checked = false;
    const target = this.dataset.target || '#CartDrawer-Checkout';
    this.checkoutButton =
      document.querySelector(target) || this.closest('form')?.querySelector('button[type="submit"], .button');
    this.disableButton = this.dataset.disableButton === 'true';
    this.warningText = this.dataset.warningText;
    this.warningTextElement = document.querySelector(
      `.tnc-checkbox-warning--${this.dataset.warningPosition}-${this.dataset.section}`
    );
    if (!this.checkoutButton || !this.warningTextElement) return;
    this.warningTextElement.innerHTML = this.warningText;
    if (this.disableButton) this.checkoutButton.classList.add('disabled');
    this.addEventListener('click', this.handleCheckboxClick.bind(this));
    this.checkoutButton.addEventListener('click', this.handleButtonClick.bind(this));
  }

  handleCheckboxClick() {
    this.checked = !this.checked;
    this.dataset.checked = this.checked;
    if (this.checked) {
      this.warningTextElement.classList.add('hidden');
      if (this.disableButton) this.checkoutButton.classList.remove('disabled');
    } else if (this.disableButton) {
      this.checkoutButton.classList.add('disabled');
    }
  }

  handleButtonClick(event) {
    if (this.checked === true) return;
    event.preventDefault();
    this.warningTextElement.classList.remove('hidden');
  }
}
// The misspelled tag name is what Shrine's markup uses.
if (!customElements.get('tnc-chekcbox')) customElements.define('tnc-chekcbox', TncCheckbox);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.duration = parseInt(this.dataset.duration);
    this.initTimer();
    this.updateTimer();
    if (this.dataset.autoPlay === 'true') this.playTimer();
  }

  initTimer() {
    this.innerHTML = '';
    this.minutesSpan = document.createElement('span');
    this.secondsSpan = document.createElement('span');
    this.append(this.minutesSpan, document.createTextNode(':'), this.secondsSpan);
  }

  updateTimer() {
    let seconds = parseInt(this.dataset.duration);
    if (seconds === 0) seconds = 90;
    this.minutesSpan.innerHTML = this.formatNumber(Math.floor(seconds / 60));
    this.secondsSpan.innerHTML = this.formatNumber(seconds % 60);
    this.dataset.duration = seconds - 1;
  }

  playTimer() {
    if (this.isPlaying) return;
    this.isPlaying = true;
    this.playInterval = setInterval(() => this.updateTimer(), 1000);
  }

  pauseTimer() {
    clearInterval(this.playInterval);
    this.isPlaying = false;
  }

  formatNumber(number) {
    return number.toString().length === 1 ? `0${number}` : number;
  }
}
if (!customElements.get('countdown-timer')) customElements.define('countdown-timer', CountdownTimer);

/* ---------- Product form ---------- */

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        this.formIdInput = this.form.querySelector('[name=id]');
        this.formIdInput.disabled = false;
        this.isMain = this.classList.contains('main-product-form');
        this.additionalAtcButtons = this.isMain ? document.querySelectorAll('.main-product-atc') : [];
        this.skipCart = this.formIdInput.dataset.skipCart === 'true';
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.isCartUpsell = this.dataset.isCartUpsell === 'true';
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
          this.hasDrawer = true;
        }
        this.loadingSpinner = this.submitButton?.querySelector('.loading-overlay__spinner');
        const section = this.dataset.section;
        this.displayedSubmitButton = document.getElementById(`DisplayedSubmitBtn-${section}`);
        this.bundleDeals = document.getElementById(`bundle-deals-${section}`);
        this.quantityBreaks = document.getElementById(`quantity-breaks-${section}`);
        this.quantityPicker = document.getElementById(`Quantity-Form-${section}`);
        this.customFields = document.querySelectorAll(`[id^='CustomField-${section}-']`);
        this.quantityGifts = document.getElementById(`quantity-gifts-${section}`);
        this.mainBundleItems = document.querySelectorAll(`.main-offer-item-${section}`);
        this.variantInputs = this.form.querySelector('.product-form__variants');
      }

      /** Collects ids of everything that rides along with the main product (upsells, gifts, bundles). */
      buildItemInputs() {
        const section = this.dataset.section;
        let extraIds = [];
        let markup = '';

        if (this.hasDrawer && !this.isCartUpsell && !this.skipCart) {
          this.cart.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach((upsell) => {
            if (upsell.dataset.selected === 'true' && !this.cart.querySelector(`.cart-item--product-${upsell.dataset.handle}`)) {
              extraIds.unshift(upsell.dataset.id);
            }
          });
        }

        if (this.bundleDeals) {
          if (this.dataset.includePdpUpsells === 'true') {
            document.querySelectorAll('product-info-upsell').forEach((upsell) => {
              if (upsell.dataset.selected === 'true') extraIds.unshift(upsell.dataset.id);
            });
          }
          extraIds.forEach((id, i) => {
            markup += `<input type="hidden" name="items[${i}][quantity]" value="1"><input type="hidden" name="items[${i}][id]" value="${id}">`;
          });
          const offset = extraIds.length;
          this.bundleDeals.formVariants.forEach((variant, i) => {
            markup += `<input type="hidden" name="items[${i + offset}][quantity]" value="${variant.quantity}"><input type="hidden" name="items[${i + offset}][id]" value="${variant.id}">`;
          });
          this.variantInputs.innerHTML = markup;
          return true;
        }

        let mainIds = [];
        if (this.quantityGifts && this.quantityGifts.unlockedItems.length > 0) {
          extraIds = [...extraIds, ...this.quantityGifts.unlockedItems];
        }

        // Add-ons and gifts attached to the selected quantity break offer.
        const selectedOffer = this.quantityBreaks
          ?.querySelector('input[name="quantity"]:checked')
          ?.closest('.quantity-break');
        const offerUpsells = selectedOffer?.querySelector('quantity-break-upsells');
        if (offerUpsells?.selectedIds) extraIds = [...extraIds, ...offerUpsells.selectedIds()];

        const upsells = document.querySelectorAll(`.product-info-upsell-${section}`);
        for (let i = upsells.length - 1; i >= 0; i--) {
          if (upsells[i].dataset.selected === 'true') extraIds.push(upsells[i].dataset.id);
        }

        if (this.mainBundleItems.length === 0) {
          if (this.quantityBreaks && this.quantityBreaks.formVariants.length > 0) {
            extraIds = [...extraIds, ...this.quantityBreaks.formVariants];
            mainIds = [...this.quantityBreaks.formVariants];
          } else if (extraIds.length > 0) {
            let quantity = 1;
            if (this.quantityBreaks) {
              quantity = this.quantityBreaks.selectedQuantity;
            } else if (this.quantityPicker) {
              quantity = parseInt(this.quantityPicker.querySelector('.quantity__input').value);
            }
            for (let i = 0; i < quantity; i++) extraIds.push(this.formIdInput.value);
            mainIds = [this.formIdInput.value];
          }
        }

        for (let i = this.mainBundleItems.length - 1; i >= 0; i--) {
          extraIds.push(this.mainBundleItems[i].dataset.id);
          mainIds.push(this.mainBundleItems[i].dataset.id);
        }

        if (extraIds.length === 0) return false;

        const items = [];
        extraIds.forEach((id) => {
          const existing = items.find((item) => item.id === id);
          if (existing) {
            existing.quantity += 1;
          } else {
            items.push({ id, quantity: 1 });
          }
        });

        const properties = [];
        this.customFields.forEach((field) => {
          if (field.dataset.disabled != 'true') properties.push({ fieldName: field.fieldName, value: field.prevValue });
        });

        let sellingPlanInput;
        let appPropertyInputs = [];
        if (this.isMain) {
          sellingPlanInput =
            this.querySelector('.appstle-active-option [name="selling_plan"]') ||
            this.querySelector('[name="selling_plan"]') ||
            document.querySelector('.sls-purchase [name="selling_plan"]');
          appPropertyInputs = document.querySelectorAll(
            '.cl-upload--wrapper input[name^="properties["], .uploadkit-attribute-container input[name^="properties["], .gpo-hidden-inputs input:not(#validator)'
          );
        }
        const simpleBundles = this.isMain ? this.querySelector('#simple-bundles-io-options') : null;
        const escape = (value) => String(value ?? '').replace(/"/g, '&quot;');

        items.forEach((item, i) => {
          markup += `<input type="hidden" name="items[${i}][quantity]" value="${item.quantity}"><input type="hidden" name="items[${i}][id]" value="${item.id}">`;
          if (!mainIds.includes(item.id)) return;
          properties.forEach((property) => {
            markup += `<input type="hidden" name="items[${i}][properties][${escape(property.fieldName)}]" value="${escape(property.value)}">`;
          });
          if (sellingPlanInput && typeof sellingPlanInput.value === 'string' && sellingPlanInput.value.length > 0) {
            markup += `<input type="hidden" name="items[${i}][selling_plan]" value="${escape(sellingPlanInput.value)}">`;
          }
          simpleBundles?.querySelectorAll('select').forEach((select) => {
            const match = select.name.match(/properties\[(.*?)\]/);
            if (match) markup += `<input type="hidden" name="items[${i}][properties][${escape(match[1])}]" value="${escape(select.value)}">`;
          });
          appPropertyInputs.forEach((input) => {
            const match = input.name.match(/properties\[(.*?)\]/);
            if (match) markup += `<input type="hidden" name="items[${i}][properties][${escape(match[1])}]" value="${escape(input.value)}">`;
          });
        });

        this.variantInputs.innerHTML = markup;
        return true;
      }

      handleSubmit(event) {
        let clickedButton = null;
        let skipCartButton = false;
        if (event) {
          event.preventDefault();
          if (event.currentTarget?.classList?.contains('button--has-spinner')) {
            clickedButton = event.currentTarget;
            clickedButton.classList.add('loading');
          }
          if (event.currentTarget?.dataset?.skipCartButton === 'true') skipCartButton = true;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        if (!clickedButton) {
          this.submitButton.classList.add('loading');
          this.loadingSpinner?.classList.remove('hidden');
        }
        this.additionalAtcButtons.forEach((button) => button.setAttribute('disabled', ''));
        if (this.displayedSubmitButton) {
          this.displayedSubmitButton.setAttribute('disabled', '');
          this.displayedSubmitButton.classList.add('loading');
        }

        let usesItemsArray = false;
        if (this.variantInputs) {
          this.variantInputs.innerHTML = '';
          usesItemsArray = this.buildItemInputs();
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        if (usesItemsArray) {
          // The items[] inputs carry everything; drop the single-item fields so the main product isn't added twice.
          const optionNames = this.dataset.options ? this.dataset.options.split(',') : [];
          ['id', 'quantity', ...optionNames].forEach((name) => formData.delete(name));
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span')?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (this.skipCart || skipCartButton) {
              window.location = '/checkout';
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => setTimeout(() => this.cart.renderContents(response, this.isCartUpsell)),
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response, this.isCartUpsell);
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (clickedButton) {
              clickedButton.classList.remove('loading');
            } else {
              this.submitButton.classList.remove('loading');
              this.loadingSpinner?.classList.add('hidden');
            }
            this.additionalAtcButtons.forEach((button) => button.removeAttribute('disabled'));
            if (this.displayedSubmitButton) {
              this.displayedSubmitButton.removeAttribute('disabled');
              this.displayedSubmitButton.classList.remove('loading');
            }
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}


/** Extra add-to-cart buttons rendered outside the main form (section ATC buttons). */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[id^="SectionAtcBtn-"]');
  if (!button) return;
  const productForm = document.querySelector('product-form[data-main="true"]');
  if (productForm) productForm.handleSubmit(event);
});

/* ---------- Search ---------- */

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => this.onChange(event), 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}
if (!customElements.get('search-form')) customElements.define('search-form', SearchForm);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;
    if (currentButtonText.split(previousTerm).length - 1 > 1) return;
    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }
    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=sh-predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-sh-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) this.setLiveRegionText(count.textContent);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    this.resultsMaxHeight = window.innerHeight - (header ? header.getBoundingClientRect().bottom : 0);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

/* ---------- Modals & media ---------- */

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('sh-deferred-media, sh-product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    // Shrine styles only apply inside a .shrine wrapper, so move into the shared one.
    window.shPortal().appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button:not(.internal-video__play, .internal-video__sound-btn)');
    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
if (!customElements.get('modal-opener')) customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (this.getAttribute('loaded')) return;
    const content = document.createElement('div');
    content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
    this.setAttribute('loaded', true);
    const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
    if (focus) deferredElement.focus();
  }
}
if (!customElements.get('sh-deferred-media')) customElements.define('sh-deferred-media', DeferredMedia);

class CopyButton extends HTMLElement {
  constructor() {
    super();
    this.textarea = document.createElement('textarea');
    this.textarea.classList.add('visually-hidden');
    this.textarea.value = (this.dataset.content || '').trim();
    this.appendChild(this.textarea);
    this.addEventListener('click', this.handleClick.bind(this));
  }

  async copy() {
    try {
      await navigator.clipboard.writeText(this.textarea.value);
    } catch (error) {
      this.textarea.select();
      document.execCommand('copy');
    }
  }

  handleClick() {
    this.copy();
    this.dataset.success = 'true';
    setTimeout(() => (this.dataset.success = 'false'), 3000);
  }
}
if (!customElements.get('copy-button')) customElements.define('copy-button', CopyButton);

class PopupCopyButton extends CopyButton {
  constructor() {
    super();
    this.copySuccessMsg = document.querySelector('.popup-modal__success-msg');
  }

  handleClick() {
    this.copy();
    if (this.copySuccessMsg) this.copySuccessMsg.style.display = 'block';
  }
}
if (!customElements.get('popup-copy-button')) customElements.define('popup-copy-button', PopupCopyButton);

class InternalVideo extends HTMLElement {
  constructor() {
    super();
    this.playButton = this.querySelector('.internal-video__play');
    this.noPlayBtn = this.dataset.noPlayBtn === 'true';
    this.loaded = false;
    this.soundButton = this.querySelector('.internal-video__sound-btn');
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.querySelector('source[type="application/x-mpegURL"]')?.remove();
    this.timeline = this.querySelector('.internal-video__timeline');
    this.dragging = false;

    this.playButton?.addEventListener('click', this.playVideo.bind(this));
    this.soundButton?.addEventListener('click', this.toggleSound.bind(this));
    this.video.addEventListener('ended', this.endedVideo.bind(this));

    if (this.timeline) {
      this.video.addEventListener('timeupdate', this.updateTimeline.bind(this));
      this.timeline.addEventListener('click', this.seekVideo.bind(this));
      this.timeline.addEventListener('mousedown', this.startDrag.bind(this));
      this.timeline.addEventListener('touchstart', this.startDrag.bind(this));
      document.addEventListener('mouseup', this.stopDrag.bind(this));
      document.addEventListener('touchend', this.stopDrag.bind(this));
      document.addEventListener('mousemove', this.drag.bind(this));
      document.addEventListener('touchmove', this.drag.bind(this));
    }

    this.video.addEventListener('waiting', this.showSpinner.bind(this));
    this.video.addEventListener('canplaythrough', this.hideSpinner.bind(this));
    this.video.addEventListener('play', this.hideSpinner.bind(this));

    if (this.dataset.autoplay === 'true' && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        root: null,
        rootMargin: '0px',
        threshold: 0.05,
      });
      this.observer.observe(this);
    }
  }

  setPlayButtonVisible(visible) {
    if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = visible ? 'visible' : 'hidden';
  }

  playVideo() {
    if (this.video.paused) {
      if (!this.loaded) {
        this.video.load();
        this.loaded = true;
      }
      this.classList.add('internal-video--playing');
      this.setPlayButtonVisible(false);
      const playPromise = this.video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.classList.add('internal-video--playing');
            this.setPlayButtonVisible(false);
          })
          .catch(() => {
            this.classList.remove('internal-video--playing');
            this.setPlayButtonVisible(true);
          });
      }
    } else {
      this.video.pause();
      this.classList.remove('internal-video--playing');
    }
  }

  endedVideo() {
    this.classList.remove('internal-video--playing');
  }

  toggleSound() {
    this.video.muted = !this.video.muted;
    this.classList.toggle('internal-video--muted', this.video.muted);
  }

  updateTimeline() {
    const percent = (this.video.currentTime / this.video.duration) * 100;
    this.style.setProperty('--completed', `${percent}%`);
  }

  startDrag(event) {
    event.preventDefault();
    this.dragging = true;
    this.drag(event);
  }

  stopDrag() {
    this.dragging = false;
  }

  drag(event) {
    if (!this.dragging) return;
    if (event.touches) event = event.touches[0];
    this.seekVideo(event);
  }

  seekVideo(event) {
    const rect = this.timeline.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    this.video.currentTime = percent * this.video.duration;
  }

  showSpinner() {
    this.classList.add('internal-video--loading');
  }

  hideSpinner() {
    this.classList.remove('internal-video--loading');
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      for (const source of this.video.querySelectorAll('source[data-src]')) {
        source.setAttribute('src', source.getAttribute('data-src'));
        source.removeAttribute('data-src');
      }
      this.video.load();
      const playPromise = this.video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.classList.remove('internal-video--playing');
            this.setPlayButtonVisible(true);
          }
        });
      }
      this.observer.disconnect();
    });
  }
}
if (!customElements.get('internal-video')) customElements.define('internal-video', InternalVideo);

/* ---------- Disclosures, menus, header ---------- */

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}
if (!customElements.get('details-disclosure')) customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}
if (!customElements.get('sh-header-menu')) customElements.define('sh-header-menu', HeaderMenu);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]')?.addEventListener('click', this.close.bind(this));
    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');
    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}
if (!customElements.get('details-modal')) customElements.define('details-modal', DetailsModal);

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('.disclosure__list-wrapper'),
    };
    if (!this.elements.button || !this.elements.panel) return;
    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
    this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  hidePanel() {
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute(
      'aria-expanded',
      (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
    );
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) this.hidePanel();
  }
}
if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.mainOpenBtn = this.mainDetailsToggle.querySelector('summary');
    this.stickyOpenBtn = this.querySelector('.drawer__sticky-open');
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll(
      'button:not(.menu-drawer__close-menu-btn):not(.drawer__sticky-open):not(.localization-v2__button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    if (!this.stickyOpenBtn) return;
    if (this.stickyOpenBtn.dataset.display === 'after_scrolling') {
      this.checkStickyBtnScroll();
      document.addEventListener('scroll', this.checkStickyBtnScroll.bind(this));
    }
    this.stickyOpenBtn.addEventListener('click', () => {
      this.mainDetailsToggle.setAttribute('open', '');
      this.openMenuDrawer(this.mainOpenBtn, true);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;
    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    let content = summaryElement.nextElementSibling;
    if (!content || content.classList.contains('hidden')) content = detailsElement.nextElementSibling;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      const focusTarget = detailsElement.querySelector('button') || content.querySelector('button') || content;
      trapFocus(content, focusTarget);
      content.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement?.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : content.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement, skipFocus = false) {
    setTimeout(() => this.mainDetailsToggle.classList.add('menu-opening'));
    summaryElement.setAttribute('aria-expanded', true);
    if (!skipFocus) trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.dataset.details
      ? document.getElementById(event.currentTarget.dataset.details)
      : event.currentTarget.closest('details');
    if (detailsElement) this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.closest('.submenu-open')?.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;
    const handleAnimation = (time) => {
      if (animationStart === undefined) animationStart = time;
      const elapsedTime = time - animationStart;
      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };
    window.requestAnimationFrame(handleAnimation);
  }

  checkStickyBtnScroll() {
    const threshold = this.mainOpenBtn.getBoundingClientRect().top + window.scrollY + 20;
    this.stickyOpenBtn.classList.toggle('hidden', !(window.scrollY > threshold));
  }
}
if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.querySelectorAll('.menu-drawer__close-menu-btn').forEach((button) =>
      button.addEventListener('click', this.closeButtonClick.bind(this))
    );
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    const wrapper = this.closest('.header-wrapper');
    this.borderOffset = this.borderOffset || (wrapper?.classList.contains('header-wrapper--border-bottom') ? 1 : 0);
    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
      this.header.classList.add('menu-open');
    }
    setTimeout(() => this.mainDetailsToggle.classList.add('menu-opening'));
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header?.classList.remove('menu-open');
  }

  closeButtonClick(event) {
    this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    this.querySelector('.header__icon--menu[aria-expanded=true]')?.setAttribute('aria-expanded', 'false');
  }
}
if (!customElements.get('sh-header-drawer')) customElements.define('sh-header-drawer', HeaderDrawer);

class ProductsMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details[id^="ProductsMegaMenu-"]');
    this.mainLink = this.querySelector('summary.header__menu-item');
    this.body = this.querySelector('.products-mega-menu__body');
    this.overlay = this.querySelector('.products-mega-menu__overlay');
    this.items = this.querySelectorAll('[id^="ProductsMegaMenu-Item"]');
    this.links = this.querySelectorAll('[id^="ProductsMegaMenu-Link"]');
    if (!this.details || !this.mainLink) return;
    this.mainLink.addEventListener('mouseover', this.openMenu.bind(this));
    this.overlay?.addEventListener('mouseover', this.closeMenu.bind(this));
    this.overlay?.addEventListener('click', this.closeMenu.bind(this));
    document.addEventListener('click', this.outsideClick.bind(this));
    this.links.forEach((link) => link.addEventListener('mouseover', this.displayContent.bind(this)));
  }

  displayContent(event) {
    this.items.forEach((item) => item.classList.remove('products-mega-menu__item--active'));
    event.target.closest('[id^="ProductsMegaMenu-Item"]').classList.add('products-mega-menu__item--active');
  }

  openMenu() {
    document.querySelectorAll('[id^="Details-HeaderMenu"], [id^="ProductsMegaMenu-"]').forEach((details) => {
      details.removeAttribute('open');
    });
    this.details.setAttribute('open', '');
  }

  closeMenu() {
    this.details.removeAttribute('open');
  }

  outsideClick(event) {
    if (!this.details.hasAttribute('open') || event.target == this.body) return;
    if (!this.body.contains(event.target)) this.closeMenu();
  }
}
if (!customElements.get('products-mega-menu')) customElements.define('products-mega-menu', ProductsMegaMenu);

/* ---------- Sticky header group ---------- */

class StickyGroupManager extends HTMLElement {
  connectedCallback() {
    this.stickyElements = [];
    this.collectStickyElements();
    if (this.stickyElements.length === 0) return;

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.updateHeightsAndOffsets();

    this.onMediaChange = this.updateHeightsAndOffsets.bind(this);
    this.mediaQuery = window.matchMedia('(max-width: 990px)');
    this.mediaQuery.addEventListener('change', this.onMediaChange);
    this.resizeObserver = new ResizeObserver(() => this.updateHeightsAndOffsets());
    this.stickyElements.forEach((item) => item.contentElement && this.resizeObserver.observe(item.contentElement));

    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, false);
    this.hideOnScrollUp = () => (this.preventReveal = true);
    this.addEventListener('preventHeaderReveal', this.hideOnScrollUp);
  }

  collectStickyElements() {
    const elements = [...this.querySelectorAll('sticky-announcement'), ...this.querySelectorAll('sticky-header')];
    elements.sort((a, b) => {
      const position = a.compareDocumentPosition(b);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    elements.forEach((element, index) => {
      const type = element.dataset.stickyType || 'none';
      if (type === 'none') return;
      const isAlwaysSticky = type === 'always' || type === 'reduce-logo-size';
      const isHeader = element.tagName.toLowerCase() === 'sticky-header';
      const contentElement = isHeader
        ? element.closest('.section-header') || element.parentElement
        : element.closest('.shopify-section') || element.parentElement;
      this.stickyElements.push({ element, contentElement, type, index, height: 0, isHidden: false, isAlwaysSticky, isHeader });
      element.dataset.managedByGroup = 'true';
      if (isAlwaysSticky && contentElement) contentElement.classList.add('shopify-section-header-sticky');
    });
  }

  updateHeightsAndOffsets() {
    let offset = 0;
    this.stickyElements.forEach((item) => {
      if (item.contentElement) {
        item.height = item.contentElement.getBoundingClientRect().height;
        const height = Math.ceil(item.height);
        item.contentElement.style.setProperty(item.isHeader ? '--header-height' : '--announcement-bar-height', `${height}px`);
      }
      const rounded = Math.floor(offset);
      item.element.style.setProperty('--sticky-offset', `${rounded}px`);
      item.contentElement?.style.setProperty('--sticky-offset', `${rounded}px`);
      if (!item.isHidden) offset += item.height;
    });
    document.documentElement.style.setProperty('--sticky-group-height', `${Math.floor(offset)}px`);
    const header = this.stickyElements.find((item) => item.isHeader);
    if (header) document.documentElement.style.setProperty('--header-height', `${Math.ceil(header.height)}px`);
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollingDown = scrollTop > this.currentScrollTop;
    const scrollingUp = scrollTop < this.currentScrollTop;
    const pastGroup = scrollTop > this.getBoundingClientRect().height;
    const atTop = scrollTop <= 0;

    const searchOpen = this.stickyElements.some((item) => item.isHeader && item.element.querySelector('predictive-search')?.isOpen);
    if (searchOpen) {
      this.currentScrollTop = scrollTop;
      return;
    }

    this.stickyElements.forEach((item) => {
      if (atTop) {
        this.resetElement(item);
      } else if (pastGroup) {
        item.contentElement?.classList.add('scrolled-past-header');
        if (scrollingDown) {
          if (this.preventHide) return;
          requestAnimationFrame(() => this.hideElement(item));
        } else if (scrollingUp) {
          if (this.preventReveal) {
            clearTimeout(this.isScrolling);
            this.isScrolling = setTimeout(() => (this.preventReveal = false), 66);
            requestAnimationFrame(() => this.hideElement(item));
          } else {
            requestAnimationFrame(() => this.revealElement(item));
          }
        }
      } else if (item.isAlwaysSticky) {
        item.contentElement?.classList.add('scrolled-past-header');
        this.revealElement(item);
      }
    });
    this.currentScrollTop = scrollTop;
  }

  hideElement(item) {
    if (item.isAlwaysSticky || !item.contentElement) return;
    item.isHidden = true;
    item.contentElement.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.updateVisibleOffsets();
    item.element.dispatchEvent(new CustomEvent('stickyHide', { bubbles: true }));
  }

  revealElement(item) {
    if (!item.contentElement) return;
    item.isHidden = false;
    item.contentElement.classList.add('shopify-section-header-sticky', 'animate');
    item.contentElement.classList.remove('shopify-section-header-hidden');
    this.updateVisibleOffsets();
    item.element.dispatchEvent(new CustomEvent('stickyReveal', { bubbles: true }));
  }

  resetElement(item) {
    if (!item.contentElement) return;
    item.contentElement.classList.remove('scrolled-past-header');
    if (!item.isAlwaysSticky) {
      item.isHidden = false;
      item.contentElement.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      this.updateVisibleOffsets();
    }
    item.element.dispatchEvent(new CustomEvent('stickyReset', { bubbles: true }));
  }

  updateVisibleOffsets() {
    let offset = 0;
    this.stickyElements.forEach((item) => {
      const rounded = Math.floor(offset);
      item.element.style.setProperty('--sticky-offset', `${rounded}px`);
      item.contentElement?.style.setProperty('--sticky-offset', `${rounded}px`);
      if (!item.isHidden) offset += item.height;
    });
    document.documentElement.style.setProperty('--sticky-group-height', `${Math.floor(offset)}px`);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
    this.removeEventListener('preventHeaderReveal', this.hideOnScrollUp);
    this.resizeObserver?.disconnect();
  }
}
if (!customElements.get('sticky-group-manager')) customElements.define('sticky-group-manager', StickyGroupManager);

class StickyAnnouncement extends HTMLElement {
  connectedCallback() {
    if (this.dataset.managedByGroup === 'true') return;
    if (this.dataset.stickyType === 'always') {
      this.querySelector('.announcement-bar')?.classList.add('shopify-section-header-sticky');
    }
  }
}
if (!customElements.get('sticky-announcement')) customElements.define('sticky-announcement', StickyAnnouncement);

/* ---------- Small product widgets used across templates ---------- */

class ClickableDiscount extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.clickable-discount__btn');
    if (!this.button) return;
    this.button.addEventListener('click', this.handleClick.bind(this));
    if (this.dataset.applied === 'true') {
      this.handleClick();
    } else {
      this.reapplyDiscountIfApplicable();
    }
  }

  handleClick() {
    this.dataset.loading = 'true';
    this.button.disabled = true;
    this.dataset.error = 'false';
    fetch(`/discount/${encodeURIComponent(this.dataset.code)}`)
      .then((response) => {
        if (!response.ok) throw new Error('Error');
        this.dataset.applied = 'true';
        sessionStorage.setItem(`discount-${this.dataset.code}-applied`, 'true');
      })
      .catch(() => {
        this.dataset.error = 'true';
        this.button.disabled = false;
      })
      .finally(() => {
        this.dataset.loading = 'false';
      });
  }

  reapplyDiscountIfApplicable() {
    const code = this.dataset.code;
    if (!sessionStorage.getItem(`discount-${code}-applied`)) return;
    this.dataset.applied = 'true';
    this.button.disabled = true;
    setTimeout(() => {
      fetch(`/discount/${encodeURIComponent(code)}`).catch(() => {
        this.dataset.applied = 'false';
        this.button.disabled = false;
      });
    }, 3000);
  }
}
if (!customElements.get('clickable-discount')) customElements.define('clickable-discount', ClickableDiscount);

class DynamicDates extends HTMLElement {
  constructor() {
    super();
    this.dateFormat = this.dataset.dateFormat;
    this.days = this.rearrangeDays((this.dataset.dayLabels || '').split(','));
    this.months = (this.dataset.monthLabels || '').split(',');
    this.elementsToChange = this.querySelectorAll('[data-dynamic-date="true"]');
    this.insertDates();
    document.addEventListener('shopify:section:load', () => this.insertDates());
  }

  ordinal(day) {
    if (day === 1 || day === 21 || day === 31) return 'st';
    if (day === 2 || day === 22) return 'nd';
    if (day === 3 || day === 23) return 'rd';
    return 'th';
  }

  pad(number) {
    return String(number).padStart(2, '0');
  }

  formatDate(date) {
    const day = date.getDate();
    switch (this.dateFormat) {
      case 'day_dd_mm':
        return `${this.days[date.getDay()]}, ${day}. ${this.months[date.getMonth()]}`;
      case 'mm_dd':
        return `${this.months[date.getMonth()]} ${day}${this.ordinal(day)}`;
      case 'dd_mm':
        return `${day}. ${this.months[date.getMonth()]}`;
      case 'dd_mm_no_dot':
        return `${day} ${this.months[date.getMonth()]}`;
      case 'day_dd_mm_numeric':
        return `${this.days[date.getDay()]}, ${this.pad(day)}. ${this.pad(date.getMonth() + 1)}.`;
      case 'dd_mm_numeric':
        return `${this.pad(day)}. ${this.pad(date.getMonth() + 1)}.`;
      default:
        return `${this.days[date.getDay()]}, ${this.months[date.getMonth()]} ${day}${this.ordinal(day)}`;
    }
  }

  insertDates() {
    const today = new Date();
    this.elementsToChange.forEach((element) => {
      const start = addDays(today, parseInt(element.dataset.minDays));
      const end = addDays(today, parseInt(element.dataset.maxDays));
      element.innerHTML = (element.dataset.text || '')
        .replace('[start_date]', this.formatDate(start))
        .replace('[end_date]', this.formatDate(end));
    });
  }

  rearrangeDays(days) {
    days.unshift(days[6]);
    days.length = 7;
    return days;
  }
}
if (!customElements.get('dynamic-dates')) customElements.define('dynamic-dates', DynamicDates);
